import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api, type TaxSummaryDto } from '../lib/api.js';
import { formatCompact, formatExact } from '../components/MoneyText.js';
import { Card, ErrorNote, Row, Skeleton } from './Home.js';

/**
 * Tax position — the one screen here that is about a DECISION rather than a
 * record.
 *
 * Everything else in ApnaKosh reports what has happened. This says what happens
 * if you act: which gains would be taxed at the short-term rate today, which
 * have crossed the twelve-month line, and — the actionable part — which units
 * are about to cross it.
 *
 * It states the law's figures and stops there. No "tax due", no suggestion to
 * sell or wait: the rate that applies depends on income this product never
 * sees, and the product is read-only by design.
 */
export function Tax() {
  const { data, isPending, error } = useQuery({
    queryKey: ['tax'],
    queryFn: () => api.get<TaxSummaryDto>('/tax'),
  });

  if (isPending) return <Skeleton />;
  if (error) return <ErrorNote message={(error as Error).message} />;

  const { shortTerm, longTerm, maturingSoon, holdings, unclassified, longTermExemptionPaise, asOf } = data;
  const headroom = longTermExemptionPaise - longTerm.gainPaise;

  return (
    <div>
      <h1 className="page-title">Tax position</h1>
      <p className="page-sub">
        Unrealised gains as of {asOf}, split at the twelve-month line. Nothing here is a filing or advice — it
        is what the statements already say, in the terms the Act uses.
      </p>

      {/* The split itself: two figures that are taxed differently. */}
      <div className="stat-grid">
        <div className="stat">
          <span className="stat-label">Short-term gain</span>
          <span className={`stat-value ${shortTerm.gainPaise >= 0 ? 'delta-pos' : 'delta-neg'}`}>
            {formatCompact(shortTerm.gainPaise)}
          </span>
          <span className="stat-note">on {formatCompact(shortTerm.valuePaise)} held under a year</span>
        </div>
        <div className="stat">
          <span className="stat-label">Long-term gain</span>
          <span className={`stat-value ${longTerm.gainPaise >= 0 ? 'delta-pos' : 'delta-neg'}`}>
            {formatCompact(longTerm.gainPaise)}
          </span>
          <span className="stat-note">on {formatCompact(longTerm.valuePaise)} held over a year</span>
        </div>
      </div>

      {/*
        The exemption is stated as a fact with headroom, never applied to
        produce a number that looks like a bill. Whether it is already used up
        elsewhere is something only the family knows.
      */}
      <Card title="The ₹1.25 lakh exemption">
        <p className="page-sub" style={{ marginTop: 0 }}>
          {headroom > 0 ? (
            <>
              Long-term equity gains up to {formatExact(longTermExemptionPaise)} are exempt in a financial year.
              Realising everything long-term today would use {formatExact(longTerm.gainPaise)} of it, leaving{' '}
              <strong>{formatExact(headroom)}</strong> unused. This ignores anything already realised this year.
            </>
          ) : (
            <>
              Long-term gains here ({formatExact(longTerm.gainPaise)}) already exceed the{' '}
              {formatExact(longTermExemptionPaise)} annual exemption by{' '}
              <strong>{formatExact(Math.abs(headroom))}</strong> if realised together.
            </>
          )}
        </p>
      </Card>

      {/*
        The actionable card. A date on which a gain changes category is the one
        thing on this screen a family can act on by doing nothing.
      */}
      {maturingSoon.length > 0 && (
        <Card title="Crossing the year soon">
          {maturingSoon.map((m) => (
            <Row
              key={m.holdingId}
              left={
                <Link to={`/wealth?open=${m.holdingId}`}>
                  <span className="row-title">{m.scheme}</span>
                  <span className="row-meta">
                    {m.member} · {m.units.toLocaleString('en-IN', { maximumFractionDigits: 3 })} units become
                    long-term on {m.on}
                  </span>
                </Link>
              }
              right={<span className="delta-pos">{formatCompact(m.gainPaise)}</span>}
            />
          ))}
        </Card>
      )}

      <Card title={`By holding — ${holdings.length}`}>
        {holdings.length === 0 && <p className="page-sub">No holding has units the equity rules apply to yet.</p>}
        {holdings.map((h) => (
          <Row
            key={h.holdingId}
            left={
              <Link to={`/wealth?open=${h.holdingId}`}>
                <span className="row-title">{h.scheme}</span>
                <span className="row-meta">
                  {h.member} · {h.folio}
                  {h.incomplete && <span className="state-warn"> · ⚠ cost incomplete</span>}
                </span>
              </Link>
            }
            right={
              <span style={{ textAlign: 'right' }}>
                <span className="money-cell">
                  {h.shortTerm.units > 0 ? formatCompact(h.shortTerm.gainPaise) : '—'}
                </span>
                <span className="row-detail state-muted">
                  {h.longTerm.units > 0 ? `${formatCompact(h.longTerm.gainPaise)} long` : 'all short'}
                </span>
              </span>
            }
          />
        ))}
      </Card>

      {/*
        Named, not hidden. Debt funds, gold and arbitrage are taxed on rules
        this screen does not implement, and quietly folding them into an equity
        total would be a confident wrong answer about money.
      */}
      {unclassified.count > 0 && (
        <p className="page-sub">
          {unclassified.count} holding{unclassified.count === 1 ? '' : 's'} worth{' '}
          {formatCompact(unclassified.valuePaise)} are left out: debt, gold, arbitrage and anything AMFI has not
          categorised follow different rules, and guessing at them would be worse than saying nothing.
        </p>
      )}
    </div>
  );
}
